import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { getApiBaseUrl } from "../config/api";
import RichTextEditor from "./shared/RichTextEditor";
import CaptionAssist from "./shared/CaptionAssist";
import FitCoverImage from "./shared/FitCoverImage";
import { canApplyForJobs, canManagePost } from "../utils/userRole";

const API_URL = getApiBaseUrl();

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: 14,
  borderRadius: 12,
  border: "1px solid rgba(148, 163, 184, 0.9)",
  marginBottom: 10,
  outline: "none",
  backgroundColor: "rgba(248, 250, 252, 0.95)",
  color: "#0f172a",
  boxSizing: "border-box",
};

function resolveCoverPreview(cover) {
  if (!cover) return null;
  const raw = String(cover).trim();
  if (!raw) return null;
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
  const base = API_URL.replace(/\/+$/, "");
  const path = raw.replace(/^\/+/, "");
  return `${base}/${path}`;
}

function skillsToInput(post) {
  if (Array.isArray(post?.looking_for_job_skills)) {
    return post.looking_for_job_skills.filter(Boolean).join(", ");
  }
  return "";
}

/**
 * Intern / user open-to-work composer: role, skills, portfolio, contact.
 */
export default function LookingForJobForm({
  user,
  onClose,
  onSuccess,
  onRefresh,
  groupId = null,
  post = null,
}) {
  const isEdit = Boolean(post?.id);
  const [title, setTitle] = useState(post?.title || "");
  const [location, setLocation] = useState(post?.looking_for_job_location || "");
  const [skills, setSkills] = useState(skillsToInput(post));
  const [portfolioUrl, setPortfolioUrl] = useState(
    post?.looking_for_job_portfolio_url || ""
  );
  const [contactEmail, setContactEmail] = useState(
    post?.looking_for_job_contact_email || user?.email || ""
  );
  const [availability, setAvailability] = useState(
    post?.looking_for_job_availability || ""
  );
  const [content, setContent] = useState(
    post?.looking_for_job_description || post?.text || ""
  );
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(
    resolveCoverPreview(post?.cover_image)
  );
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const previewRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  useEffect(() => {
    if (!user?.id || isEdit) return undefined;
    let cancelled = false;

    axios
      .get(`${API_URL}/get_user_profile.php?user_id=${user.id}`)
      .then((res) => {
        if (cancelled || !res.data?.success) return;
        const profile = res.data.user || {};
        const skillList = Array.isArray(profile.skills)
          ? profile.skills.map((s) => s.skill_name || s.name || s).filter(Boolean)
          : [];
        if (skillList.length) {
          setSkills((prev) => prev || skillList.join(", "));
        }
        if (profile.email) {
          setContactEmail((prev) => prev || profile.email);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.id, isEdit]);

  if (!canApplyForJobs(user) && !(isEdit && canManagePost(user, post))) {
    return null;
  }

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Cover must be an image.");
      return;
    }
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    const url = URL.createObjectURL(file);
    previewRef.current = url;
    setCoverFile(file);
    setCoverPreview(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.email && !user?.username) {
      alert("You must be logged in");
      return;
    }
    const plain = content.replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").trim();
    if (!title.trim() || !plain) {
      alert("Please add a desired role and a short intro.");
      return;
    }

    setLoading(true);
    setStatus(isEdit ? "Saving open-to-work post..." : "Publishing open-to-work post...");
    try {
      const formData = new FormData();
      user.email && formData.append("email", user.email);
      user.username && formData.append("username", user.username);
      user.country && formData.append("country", user.country);
      formData.append("title", title.trim());
      formData.append("content", content);
      formData.append("category", "Looking for job");
      formData.append("post_type", "looking_for_job");
      formData.append("location", location.trim());
      formData.append("skills", skills.trim());
      formData.append("portfolio_url", portfolioUrl.trim());
      formData.append("contact_email", contactEmail.trim());
      formData.append("availability", availability.trim());
      if (user?.id) formData.append("user_id", user.id);
      if (isEdit) formData.append("post_id", post.id);
      if (groupId) {
        formData.append("group_id", groupId);
        if (!isEdit) formData.append("user_id", user.id);
      }

      if (coverFile) {
        setStatus("Compressing cover...");
        try {
          const compressed = await imageCompression(coverFile, {
            maxSizeMB: 1.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
          formData.append("cover", compressed, compressed.name);
        } catch {
          formData.append("cover", coverFile);
        }
      }

      setStatus("Uploading...");
      const endpoint = isEdit
        ? groupId
          ? `${API_URL}/update_group_post.php`
          : `${API_URL}/update_post.php`
        : groupId
          ? `${API_URL}/create_group_post.php`
          : `${API_URL}/create_post.php`;
      const { data } = await axios.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });

      if (data.success) {
        onSuccess?.(data.post);
        onRefresh?.();
        onClose?.();
      } else {
        alert(data.message || (isEdit ? "Failed to update looking-for-job post" : "Failed to publish looking-for-job post"));
      }
    } catch (err) {
      console.error(err);
      alert("Upload error: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      setStatus("");
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: 24,
          width: "100%",
          maxWidth: 720,
          maxHeight: "92vh",
          overflowY: "auto",
          position: "relative",
          boxShadow: "0 25px 50px rgba(15,23,42,0.35)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 14,
            border: "none",
            background: "transparent",
            fontSize: 22,
            cursor: "pointer",
            color: "#64748b",
          }}
        >
          ×
        </button>

        <h2 style={{ margin: "0 0 6px", fontSize: 22, color: "#0f172a" }}>
          {isEdit ? "Edit open-to-work post" : "Looking for a job"}
        </h2>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
          {isEdit
            ? "Update your role, skills, and how employers can reach you."
            : "Share your skills and portfolio so employers can contact you."}
        </p>

        <form onSubmit={handleSubmit}>
          <input
            style={inputStyle}
            placeholder="Desired role * (e.g. Frontend Intern)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          <input
            style={inputStyle}
            placeholder="Preferred location (e.g. Remote, Singapore)"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
          <input
            style={inputStyle}
            placeholder="Skills (comma-separated, e.g. React, PHP, Figma)"
            value={skills}
            onChange={(e) => setSkills(e.target.value)}
          />
          <input
            style={inputStyle}
            type="url"
            placeholder="Portfolio / LinkedIn / GitHub URL"
            value={portfolioUrl}
            onChange={(e) => setPortfolioUrl(e.target.value)}
          />
          <input
            style={inputStyle}
            type="email"
            placeholder="Contact email for employers"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
          <input
            style={inputStyle}
            placeholder="Availability (e.g. Immediate, From Aug 2026)"
            value={availability}
            onChange={(e) => setAvailability(e.target.value)}
          />

          <label style={{ display: "block", fontSize: 13, marginBottom: 6, color: "#475569" }}>
            Cover image (optional)
          </label>
          <input type="file" accept="image/*" onChange={handleCoverChange} style={{ marginBottom: 10 }} />
          <FitCoverImage src={coverPreview} maxHeight={220} radius={10} marginBottom={12} />

          <label style={{ display: "block", fontSize: 13, marginBottom: 6, color: "#475569" }}>
            About you / what you&apos;re looking for *
          </label>
          <RichTextEditor content={content} onChange={setContent} />
          <CaptionAssist
            kind="open_to_work"
            format="html"
            text={content}
            title={title}
            context={[
              location && `Preferred location: ${location}`,
              skills && `Skills: ${skills}`,
              availability && `Availability: ${availability}`,
            ]
              .filter(Boolean)
              .join("\n")}
            userId={user?.id || user?.user_id}
            disabled={loading}
            style={{ marginTop: 8 }}
            onApply={setContent}
            onApplyTitle={(nextTitle) => {
              if (!title.trim() && nextTitle) setTitle(nextTitle);
            }}
          />

          {status && (
            <p style={{ margin: "10px 0 0", fontSize: 13, color: "#0369a1" }}>{status}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: 16,
              width: "100%",
              padding: "12px 16px",
              borderRadius: 10,
              border: "none",
              background: loading ? "#94a3b8" : "#1d4ed8",
              color: "#fff",
              fontWeight: 650,
              fontSize: 15,
              cursor: loading ? "default" : "pointer",
            }}
          >
            {loading
              ? isEdit
                ? "Saving…"
                : "Publishing…"
              : isEdit
                ? "Save open-to-work post"
                : "Publish open-to-work post"}
          </button>
        </form>
      </div>
    </div>
  );
}
